import React, { useEffect, useState, useMemo } from "react";
import { Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import IconDefinition from "../iconDefinition/IconDefinition";
// INTERFACES
import { RootState } from "../../../app/store";
import IconDefinitionEscalatorDefault from "../iconDefinition/IconDefinitionEscalatorDefault";
import MonitoringIconsMovingWalks from "../monitoringIcons/MonitoringIconsMovingWalks";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { BLANK_LIFT, LiftType } from "../../../constants/lifts";
import { stringToUuidT } from "../../../utils/uuid";

function toRgba(hexColor: string, opacityPercent: number): string {
  const sanitized = (hexColor || "").replace("#", "").trim();
  const alpha = Math.min(Math.max(opacityPercent, 0), 100) / 100;
  if (!sanitized || alpha <= 0) {
    return "rgba(0, 0, 0, 0)";
  }

  let expanded = sanitized;
  if (sanitized.length === 3) {
    expanded = sanitized
      .split("")
      .map((char) => `${char}${char}`)
      .join("");
  }

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    return "rgba(0, 0, 0, 0)";
  }

  const r = Number.parseInt(expanded.slice(0, 2), 16);
  const g = Number.parseInt(expanded.slice(2, 4), 16);
  const b = Number.parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resolveLiftType(value: unknown): LiftType | null {
  if (typeof value === "number") {
    if (
      value === LiftType.ELEVATOR ||
      value === LiftType.ESCALATOR ||
      value === LiftType.MOVINGWALK
    ) {
      return value;
    }
    return null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.toLowerCase() === "map") {
    return LiftType.ELEVATOR;
  }

  const parsed = Number(trimmed);
  if (Number.isFinite(parsed)) {
    return resolveLiftType(parsed);
  }

  const enumValue = (LiftType as unknown as Record<string, unknown>)[trimmed.toUpperCase()];
  return typeof enumValue === "number" ? (enumValue as LiftType) : null;
}

function MonitoringMapViewPin3({
  carName,
  icon,
  direction,
  orientation,
  liftId,
  liftType,
  fixedFloorLabel = false,
  mapBackgroundColorHex = "",
  mapBackgroundOpacity = 0,
  forceDisplayName = false,
  forceHideName = false,
  mapTitleFontSize = 10,
  mapTitleColorHex = "#000000",
  mapTitleRotationDeg = 0,
}: {
  carName: string;
  icon: string;
  direction: "top" | "left" | "bottom" | "right";
  orientation: "horizontal" | "vertical";
  liftId: string;
  liftType: string | number;
  fixedFloorLabel?: boolean;
  mapBackgroundColorHex?: string;
  mapBackgroundOpacity?: number;
  forceDisplayName?: boolean;
  forceHideName?: boolean;
  mapTitleFontSize?: number;
  mapTitleColorHex?: string;
  mapTitleRotationDeg?: number;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [showContent, setShowContent] = useState<boolean>(false);
  const [boxSize, setBoxSize] = useState<"sm" | "md" | "lg">("sm");
  const [canShowServiceMode, setCanShowServiceMode] = useState<boolean>(false);
  const [canShowName, setCanShowName] = useState<boolean>(false);
  const [canShowFloors, setCanShowFloors] = useState<boolean>(false);

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { serviceModeEnabled, floorsEnabled, namesEnabled } = useSelector(
    (state: any) => state.mapView,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(liftId), [liftId]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = socketMessageLift.faults;

  const hasUpDownMovement =
    socketMessageLift.direction === Usage.Up || socketMessageLift.direction === Usage.Down;
  const serviceModeInfo = useMemo(
    () =>
      getServiceModeDisplay(
        socketMessageLift.serviceMode?.messageType ?? null,
        socketMessageLift.serviceMode?.message?.message?.message ?? null,
      ),
    [socketMessageLift.serviceMode],
  );
  const currentFloorLabel = useMemo(() => {
    if (socketMessageLift.position === null || socketMessageLift.position === undefined) {
      return "--";
    }

    return String(socketMessageLift.position);
  }, [socketMessageLift.position]);

  const shouldShowLegacyFloors = canShowFloors && !fixedFloorLabel;
  const resolvedLiftType = useMemo(() => resolveLiftType(liftType), [liftType]);
  const isElevatorOrMap =
    resolvedLiftType === LiftType.ELEVATOR ||
    (typeof liftType === "string" && liftType.toLowerCase() === "map");
  const isEscalator = resolvedLiftType === LiftType.ESCALATOR;
  const isMovingWalk = resolvedLiftType === LiftType.MOVINGWALK;
  let iconLiftTypeLabel = LiftType[LiftType.ELEVATOR];
  if (resolvedLiftType === LiftType.ESCALATOR) {
    iconLiftTypeLabel = LiftType[LiftType.ESCALATOR];
  } else if (resolvedLiftType === LiftType.MOVINGWALK) {
    iconLiftTypeLabel = LiftType[LiftType.MOVINGWALK];
  }
  const mapBackgroundStyle = useMemo<React.CSSProperties>(
    () => ({
      backgroundColor: toRgba(mapBackgroundColorHex, mapBackgroundOpacity),
      borderColor: mapBackgroundOpacity > 0 ? mapBackgroundColorHex : "transparent",
    }),
    [mapBackgroundColorHex, mapBackgroundOpacity],
  );
  const mapTitleStyle = useMemo<React.CSSProperties>(
    () => ({
      fontSize: `${Math.max(8, mapTitleFontSize)}px`,
      color: mapTitleColorHex,
      transform: `rotate(${mapTitleRotationDeg}deg)`,
      transformOrigin: "center center",
    }),
    [mapTitleColorHex, mapTitleFontSize, mapTitleRotationDeg],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleMouseOver = () => {
    setShowContent(true);
  };
  const handleMouseLeave = () => {
    setShowContent(false);
  };
  const getPinClass = () => {
    let pinClass = "mapViewStyles__box";
    pinClass = pinClass.concat(" mapViewStyles__box__", theme);
    if (showContent || canShowServiceMode || shouldShowLegacyFloors || canShowName) {
      pinClass = pinClass.concat(
        " mapViewStyles__box__",
        boxSize,
        "__",
        orientation,
        "__",
        "expanded",
      );
    } else {
      pinClass = pinClass.concat(" mapViewStyles__box__minimized");
    }
    if (hasUpDownMovement) {
      pinClass = pinClass.concat(" mapViewStyles__arrow__", direction, "__", theme);
    }
    pinClass = pinClass.concat(" mapViewStyles__box__", boxSize, "__", orientation);
    if (hasFaults) {
      pinClass = pinClass.concat(" mapViewStyles__box__error__", direction, "__", theme);
    }
    return pinClass;
  };

  const getAlignItemClass = () => {
    if (direction === "top") {
      return "flex-start";
    }
    if (direction === "bottom") {
      return "flex-end";
    }
    return "center";
  };

  const getJustifyContentClass = () => {
    if (direction === "top") {
      return "center";
    }
    if (direction === "bottom") {
      return "center";
    }
    if (direction === "right") {
      return "flex-end";
    }
    if (direction === "left") {
      return "flex-start";
    }
    return "center";
  };

  const getAlignItems = () => {
    if (direction === "top") {
      return "flex-start";
    }
    if (direction === "bottom") {
      return "flex-end";
    }
    return "center";
  };

  const getJustifyItems = () => {
    if (direction === "left") {
      return "flex-start";
    }
    if (direction === "right") {
      return "flex-end";
    }
    return "center";
  };

  const adjustSize = () => {
    if (canShowServiceMode && canShowName && shouldShowLegacyFloors) {
      setBoxSize("lg");
    } else if (
      (canShowServiceMode && canShowName) ||
      (canShowServiceMode && shouldShowLegacyFloors) ||
      (canShowName && shouldShowLegacyFloors)
    ) {
      setBoxSize("md");
    } else {
      setBoxSize("sm");
    }
  };
  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if ((serviceModeEnabled || showContent) && socketMessageLift.serviceMode !== null)
      setCanShowServiceMode(true);
    else setCanShowServiceMode(false);

    if (forceHideName) setCanShowName(false);
    else if (forceDisplayName && carName && carName !== "") setCanShowName(true);
    else if ((namesEnabled || showContent) && carName && carName !== "") setCanShowName(true);
    else setCanShowName(false);

    if (!fixedFloorLabel && (floorsEnabled || showContent)) setCanShowFloors(true);
    else setCanShowFloors(false);

    adjustSize();
  }, [
    serviceModeEnabled,
    showContent,
    namesEnabled,
    carName,
    floorsEnabled,
    fixedFloorLabel,
    forceDisplayName,
    forceHideName,
    hasUpDownMovement,
  ]);

  useEffect(() => {
    adjustSize();
  }, [canShowServiceMode, canShowName, canShowFloors, fixedFloorLabel]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      style={{
        width: orientation === "vertical" ? 40 : 72,
        height: orientation === "vertical" ? 72 : 40,
        display: "flex",
        alignItems: orientation === "vertical" ? getAlignItems() : "",
        justifyContent: orientation !== "vertical" ? getJustifyItems() : "",
      }}
    >
      {/** ************************************************************** */}
      {/** VERTICAL */}
      {orientation === "vertical" && (
        <div
          className={`mapViewStyles__box__${boxSize}__vertical__expanded`}
          style={{
            justifyContent: getJustifyContentClass(),
            alignItems: getAlignItemClass(),
            display: "flex",
            borderRadius: "8px",
          }}
        >
          <div
            className={getPinClass()}
            style={mapBackgroundStyle}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            onFocus={emptyFunctionDeclaration}
          >
            {/** CAR NAME */}
            {canShowName && (
              <div
                style={{
                  height: "16px",
                  width: "90%",
                  marginLeft: "5%",
                  marginRight: "5%",
                  paddingTop: "3px",
                  ...mapTitleStyle,
                }}
                className="generalStyles__textEllipsis"
              >
                {carName.length >= 8 ? (
                  <Tooltip placement="top" title={carName}>
                    <p>{carName}</p>
                  </Tooltip>
                ) : (
                  <p>{carName}</p>
                )}
              </div>
            )}
            {/** ICON */}
            {icon && (
              <div
                style={{
                  width: "100%",
                }}
              >
                {isElevatorOrMap && (
                  <IconDefinition
                    isMap
                    expanded={showContent}
                    liftType={iconLiftTypeLabel}
                    icon={icon}
                    iconSize={26}
                  />
                )}
                {isEscalator && (
                  <IconDefinitionEscalatorDefault
                    iconSize={28}
                    mapViewClass={
                      showContent || namesEnabled
                        ? "mapViewStyles__icon__escalator__expanded"
                        : "mapViewStyles__icon__escalator__minimized"
                    }
                    lift={BLANK_LIFT}
                  />
                )}
                {isMovingWalk && (
                  <MonitoringIconsMovingWalks
                    direction={socketMessageLift.direction}
                    iconSize={28}
                    mapViewClass={
                      showContent || namesEnabled
                        ? "mapViewStyles__icon__escalator__expanded"
                        : "mapViewStyles__icon__escalator__minimized"
                    }
                  />
                )}
              </div>
            )}
            {/** NEXT STOP */}
            {shouldShowLegacyFloors && (
              <Tooltip placement="bottom" title="First stop > Second Stop">
                <div
                  className="generalStyles__flex"
                  style={{
                    fontSize: "10px",
                    width: "100%",
                    height: "16px",
                  }}
                >
                  <div className="" style={{ width: "33%", textAlign: "center" }}>
                    <p>First stop</p>{" "}
                  </div>
                  <div className="" style={{ width: "33%", textAlign: "center" }}>
                    <FontAwesomeIcon icon={faCaretRight} />
                  </div>
                  <div className="" style={{ width: "33%", textAlign: "center" }}>
                    <p>Second Stop</p>
                  </div>
                </div>
              </Tooltip>
            )}
            {fixedFloorLabel && isElevatorOrMap && (
              <div
                style={{
                  height: "16px",
                  width: "100%",
                  fontSize: "10px",
                  textAlign: "center",
                }}
              >
                {`Floor: ${currentFloorLabel}`}
              </div>
            )}
            {/** SERVICE MODE */}
            {canShowServiceMode && serviceModeInfo && (
              <Tooltip placement="bottom" title={serviceModeInfo.displayName}>
                <div
                  style={{
                    height: "16px",
                    width: "100%",
                    fontSize: "10px",
                    color: serviceModeInfo.color || "cyan",
                  }}
                >
                  {serviceModeInfo.abbreviation.substring(0, 3)}
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      )}
      {/** ************************************************************** */}
      {/** HORIZONTAL */}
      {orientation === "horizontal" && (
        <div
          className={`mapViewStyles__box__${boxSize}__horizontal__expanded`}
          style={{
            justifyContent: getJustifyContentClass(),
            alignItems: getAlignItemClass(),
            display: "flex",
            borderRadius: "8px",
          }}
        >
          {" "}
          <div
            className={getPinClass()}
            style={mapBackgroundStyle}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            onFocus={emptyFunctionDeclaration}
          >
            {/** CAR NAME */}
            {canShowName && (
              <div
                style={{
                  height: "16px",
                  width: "90%",
                  marginLeft: "5%",
                  marginRight: "5%",
                  paddingTop: "3px",
                  ...mapTitleStyle,
                }}
                className="generalStyles__textEllipsis"
              >
                {carName.length >= 8 ? (
                  <Tooltip placement="top" title={carName}>
                    <p>{carName}</p>
                  </Tooltip>
                ) : (
                  <p>{carName || "--"}</p>
                )}
              </div>
            )}
            <div className="generalStyles__flex">
              {/** NEXT STOP */}
              {shouldShowLegacyFloors && (
                <Tooltip placement="bottom" title="F > NST">
                  <div
                    className="generalStyles__flex"
                    style={{
                      fontSize: "10px",
                      width: "40px",
                      height: "32px",
                      paddingTop: "4px",
                    }}
                  >
                    <div className="" style={{ width: "33%", textAlign: "center" }}>
                      <p>FS</p>{" "}
                    </div>
                    <div className="" style={{ width: "33%", textAlign: "center" }}>
                      <FontAwesomeIcon icon={faCaretRight} />
                    </div>
                    <div className="" style={{ width: "33%", textAlign: "center" }}>
                      <p>SS</p>
                    </div>
                  </div>
                </Tooltip>
              )}
              {/** ICON */}
              {icon && (
                <div
                  style={{
                    width: "40px",
                  }}
                >
                  {isElevatorOrMap && (
                    <IconDefinition
                      isMap
                      expanded={showContent}
                      liftType={iconLiftTypeLabel}
                      icon={icon}
                      iconSize={36}
                    />
                  )}
                  {isEscalator && (
                    <IconDefinitionEscalatorDefault
                      iconSize={28}
                      lift={BLANK_LIFT}
                      mapViewClass={
                        showContent || namesEnabled
                          ? "mapViewStyles__icon__escalator__expanded"
                          : "mapViewStyles__icon__escalator__minimized"
                      }
                    />
                  )}
                  {isMovingWalk && (
                    <MonitoringIconsMovingWalks
                      direction={socketMessageLift.direction}
                      iconSize={28}
                      mapViewClass={
                        showContent || namesEnabled
                          ? "mapViewStyles__icon__escalator__expanded"
                          : "mapViewStyles__icon__escalator__minimized"
                      }
                    />
                  )}
                </div>
              )}
              {/** SERVICE MODE */}
              {canShowServiceMode && serviceModeInfo && (
                <Tooltip placement="bottom" title={serviceModeInfo.displayName}>
                  <div
                    style={{
                      height: "16px",
                      width: "100%",
                      fontSize: "10px",
                      color: serviceModeInfo.color || "cyan",
                    }}
                  >
                    {serviceModeInfo.abbreviation.substring(0, 3)}
                  </div>
                </Tooltip>
              )}
            </div>
            {fixedFloorLabel && isElevatorOrMap && (
              <div
                style={{
                  height: "16px",
                  width: "100%",
                  fontSize: "10px",
                  textAlign: "center",
                }}
              >
                {`Floor: ${currentFloorLabel}`}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
export default MonitoringMapViewPin3;
